import { useEffect, useState } from "react";
import { getLitigation } from "../api";
import type { LitigationApiItem, LitigationFacilityRow, MarkerRiskLevel } from "../types/litigation";

export type LitigationQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: LitigationFacilityRow[] };

/**
 * Resolves the raw `risk_level` returned by the API into the internal
 * MarkerRiskLevel union used by the table badge, filter options, and
 * map markers. Anything unexpected falls through to "weak-match" so
 * the UI stays consistent if the backend evolves.
 */
function resolveRiskLevel(raw: string | null | undefined): MarkerRiskLevel {
    switch (raw) {
        case "Low Risk":
            return "low";
        case "Medium Risk":
            return "medium";
        case "High Risk":
            return "high";
        // "Almost High" folds into High — the design dropped the orange
        // mid-tier (Andressa + Linda review). Kept as explicit cases so
        // these don't silently fall through to "weak-match".
        case "Almost High":
        case "Almost High Risk":
            return "high";
        default:
            return "weak-match";
    }
}

function mapApiToRow(item: LitigationApiItem): LitigationFacilityRow {
    return {
        locationId: item.location_id,
        type: item.type ?? "",
        facilityName: item.location_name ?? "",
        address: item.address ?? "",
        city: item.city ?? "",
        county: item.county ?? "",
        state: item.state ?? "",
        licensedBeds: item.licensed_beds ?? 0,
        lawsuitsPer100Beds: `${item.lawsuits_per_100_beds ?? 0}/100`,
        riskLevel: resolveRiskLevel(item.risk_level),
        lat: item.latitude ?? 0,
        lng: item.longitude ?? 0,
    };
}

/**
 * Fetches `/v2/submissions/{id}/litigation/?years=N`. Year is
 * server-driven (changing it triggers a refetch); every other filter is
 * client-side and lives in the consuming component.
 */
export function useLitigationData(submissionId: string | undefined, years: number): LitigationQueryState {
    const [state, setState] = useState<LitigationQueryState>({ status: "loading" });

    useEffect(() => {
        if (!submissionId) return;
        let cancelled = false;
        setState({ status: "loading" });
        getLitigation(submissionId, years)
            .then((items) => {
                if (cancelled) return;
                setState({ status: "ready", data: items.map(mapApiToRow) });
            })
            .catch((error: Error) => {
                if (!cancelled) setState({ status: "error", error });
            });
        return () => {
            cancelled = true;
        };
    }, [submissionId, years]);

    return state;
}
